import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import { ESTADOS_BR } from './imoveis/constantes';

function formatarDataHora(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]} às ${m[4]}:${m[5]}` : '';
}

// Bot do Telegram (Fase 1, 28/09/2026 — ver PLANO-BOT-TELEGRAM-IMOVEIS.md): só o vínculo de conta
// por enquanto. Só aparece pra quem tem a permissão `bot_telegram` (Roles, por usuário e por empresa).
function CardTelegram() {
  const toast = useToast();
  const confirmar = useConfirm();
  const [status, setStatus] = useState(null); // { vinculado, criadoEm }
  const [link, setLink] = useState(null);
  const [carregando, setCarregando] = useState(false);

  function carregarStatus() {
    api.get('/telegram/vinculo').then((r) => setStatus(r.data)).catch(() => {});
  }
  useEffect(carregarStatus, []);

  async function gerarLink() {
    setCarregando(true);
    try {
      const r = await api.post('/telegram/gerar-link');
      setLink(r.data.link);
    } catch (err) {
      toast.erro(err.message || 'Não foi possível gerar o link');
    } finally {
      setCarregando(false);
    }
  }

  async function desconectar() {
    if (!(await confirmar('Desconectar o Telegram desta conta?'))) return;
    try {
      await api.delete('/telegram/vinculo');
      setLink(null);
      carregarStatus();
      toast.sucesso('Telegram desconectado.');
    } catch (err) {
      toast.erro(err.message || 'Não foi possível desconectar');
    }
  }

  if (!status) return null;

  return (
    <Card className="mb-3">
      <Card.Header>Bot do Telegram</Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Conecte sua conta ao bot <strong>@eimovelpainel_bot</strong> para cadastrar imóveis em
          rascunho direto pelo Telegram (texto, fotos e áudio).
        </p>
        {status.vinculado ? (
          <>
            <Alert variant="success" className="mb-2">
              Conectado desde {formatarDataHora(status.criadoEm)}.
            </Alert>
            <Button variant="outline-danger" size="sm" onClick={desconectar}>
              Desconectar
            </Button>
          </>
        ) : link ? (
          <Alert variant="info" className="mb-0">
            <a href={link} target="_blank" rel="noreferrer">
              Abrir no Telegram para conectar
            </a>{' '}
            — o link vale por poucos minutos. Abra pelo celular onde o Telegram está instalado.
          </Alert>
        ) : (
          <Button variant="outline-primary" size="sm" onClick={gerarLink} disabled={carregando}>
            {carregando ? 'Gerando…' : 'Gerar link para conectar'}
          </Button>
        )}
      </Card.Body>
    </Card>
  );
}

// 5519999998888 -> "+55 (19) 99999-8888" (aceita também o telefone sem o nono dígito, 12 dígitos)
function formatarTelefoneBR(t) {
  const d = String(t || '').replace(/\D/g, '');
  const m = d.match(/^55(\d{2})(\d{4,5})(\d{4})$/);
  return m ? `+55 (${m[1]}) ${m[2]}-${m[3]}` : d ? `+${d}` : '';
}

// Bot do WhatsApp (25/10/2026): mesmo cadastro de imóvel em rascunho do bot do Telegram, mas conversando
// pelo WhatsApp (app WhatsLight desktop). O corretor informa o PRÓPRIO WhatsApp, recebe uma palavra e manda
// "VINCULAR <PALAVRA>" pro bot — tocando no link wa.me, a mensagem já abre pronta. O bot só aceita se vier
// desse mesmo telefone. Só aparece com a permissão `bot_whatsapp`.
function CardWhatsapp() {
  const toast = useToast();
  const confirmar = useConfirm();
  const [status, setStatus] = useState(null); // { vinculado, criadoEm }
  const [aberto, setAberto] = useState(false); // modal
  const [telefone, setTelefone] = useState('');
  const [codigo, setCodigo] = useState(null); // { codigo, comando, numero, link, telefone }
  const [carregando, setCarregando] = useState(false);

  function carregarStatus() {
    api.get('/whatsapp/vinculo').then((r) => setStatus(r.data)).catch(() => {});
  }
  useEffect(carregarStatus, []);

  // Com o código na tela, confere a cada poucos segundos se o corretor já mandou a mensagem — quando o
  // vínculo aparece, o modal fecha sozinho e o card mostra "Conectado".
  useEffect(() => {
    if (!aberto || !codigo) return undefined;
    const id = setInterval(carregarStatus, 4000);
    return () => clearInterval(id);
  }, [aberto, codigo]);
  useEffect(() => {
    if (status?.vinculado && aberto) {
      setAberto(false);
      setCodigo(null);
      toast.sucesso('WhatsApp conectado!');
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  function abrirModal() {
    setCodigo(null);
    setAberto(true);
  }

  async function gerarCodigo(e) {
    e.preventDefault();
    setCarregando(true);
    try {
      const r = await api.post('/whatsapp/gerar-link', { telefone });
      setCodigo(r.data);
    } catch (err) {
      toast.erro(err.message || 'Não foi possível gerar o código');
    } finally {
      setCarregando(false);
    }
  }

  async function desconectar() {
    if (!(await confirmar('Desconectar o WhatsApp desta conta?'))) return;
    try {
      await api.delete('/whatsapp/vinculo');
      setCodigo(null);
      carregarStatus();
      toast.sucesso('WhatsApp desconectado.');
    } catch (err) {
      toast.erro(err.message || 'Não foi possível desconectar');
    }
  }

  if (!status) return null;

  return (
    <Card className="mb-3">
      <Card.Header>Bot do WhatsApp</Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Cadastre imóveis em rascunho conversando pelo WhatsApp: fotos, localização e os detalhes por áudio ou
          texto. O bot só responde ao WhatsApp que você conectar aqui.
        </p>
        {status.vinculado ? (
          <>
            <Alert variant="success" className="mb-2">
              Conectado ao WhatsApp <strong>{formatarTelefoneBR(status.telefone)}</strong> desde {formatarDataHora(status.criadoEm)}.
              Daquele número, mande <strong>novo</strong> para o bot para cadastrar um imóvel.
            </Alert>
            <Button variant="outline-danger" size="sm" onClick={desconectar}>
              Desconectar
            </Button>
          </>
        ) : (
          <Button variant="outline-primary" size="sm" onClick={abrirModal}>
            Conectar meu WhatsApp
          </Button>
        )}
      </Card.Body>

      <Modal show={aberto} onHide={() => setAberto(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Conectar o WhatsApp</Modal.Title>
        </Modal.Header>
        {!codigo ? (
          <Form onSubmit={gerarCodigo}>
            <Modal.Body>
              <Form.Group controlId="whatsapp-telefone">
                <Form.Label>Qual é o seu WhatsApp?</Form.Label>
                <Form.Control
                  type="tel"
                  inputMode="tel"
                  autoFocus
                  required
                  placeholder="(19) 99999-8888"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Com DDD. É deste número que você vai mandar as mensagens para o bot — ele só aceita a conexão se
                  vier dele.
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setAberto(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={carregando || !telefone.trim()}>
                {carregando ? 'Gerando…' : 'Gerar código'}
              </Button>
            </Modal.Footer>
          </Form>
        ) : (
          <>
            <Modal.Body className="text-center">
              <p className="mb-1 text-muted">Seu código</p>
              <p className="display-5 fw-bold mb-3" style={{ letterSpacing: '0.1em' }}>{codigo.codigo}</p>
              {codigo.link ? (
                <>
                  <p>
                    Pelo celular do número <strong>{codigo.telefone}</strong>, toque no botão: o WhatsApp abre com a
                    mensagem pronta — é só enviar.
                  </p>
                  <Button as="a" href={codigo.link} target="_blank" rel="noreferrer" variant="success" size="lg">
                    Abrir o WhatsApp
                  </Button>
                </>
              ) : (
                <p>
                  Pelo WhatsApp do número <strong>{codigo.telefone}</strong>, mande para o número do bot a mensagem:
                  <br />
                  <code className="fs-5">{codigo.comando}</code>
                </p>
              )}
              <p className="text-muted small mt-3 mb-0">
                O código vale por {Math.max(1, Math.round((codigo.expiraEmSegundos || 600) / 60))} minutos e só funciona uma vez. Esta janela fecha sozinha quando a conexão for
                feita.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setCodigo(null)}>
                Gerar outro
              </Button>
              <Button variant="outline-secondary" onClick={() => setAberto(false)}>
                Fechar
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </Card>
  );
}

// "Perfil" do painel legado: o próprio usuário edita os próprios dados de contato
// (qualquer nível). E-mail e nível só aparecem; bloqueio e rodízio são do administrador.
// A senha fica em "Alterar senha" (o legado misturava as duas coisas).
const NIVEL_ROTULO = { 1: 'Superadmin', 2: 'Admin da imobiliária', 3: 'Corretor' };

const CAMPOS = [
  'name', 'celular', 'telegram', 'email_trello', 'creci', 'estado',
  'modelo_msg_email', 'modelo_msg_whats', 'modelo_msg_sms',
];

export default function Perfil() {
  const toast = useToast();
  const { usuario, atualizarUsuario } = useAuth();
  const [dados, setDados] = useState(null);
  const [v, setV] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    api
      .get('/perfil')
      .then((r) => {
        setDados(r.data);
        setV(Object.fromEntries(CAMPOS.map((c) => [c, r.data[c] ?? ''])));
      })
      .catch((err) => {
        setErro(true);
        toast.erro(err.message || 'Não foi possível carregar o perfil');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (campo, valor) => setV((atual) => ({ ...atual, [campo]: valor }));

  async function salvar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      const r = await api.put('/perfil', v);
      // o nome aparece no topo do painel
      atualizarUsuario({ ...usuario, nome: r.usuario.nome });
      toast.sucesso('Perfil alterado com sucesso!');
    } catch (err) {
      toast.erro(err.message || 'Não foi possível salvar o perfil');
    } finally {
      setSalvando(false);
    }
  }

  if (erro) return null;
  if (!dados) return <Spinner animation="border" />;

  return (
    <>
      <h1 className="h4 mb-4">Meu perfil</h1>
      <Form onSubmit={salvar}>
        <Card className="mb-3">
          <Card.Header>Dados pessoais</Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group controlId="perfil-nome">
                  <Form.Label>Seu nome</Form.Label>
                  <Form.Control value={v.name} onChange={(e) => set('name', e.target.value)} required autoComplete="off" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="perfil-email">
                  <Form.Label>E-mail (login)</Form.Label>
                  <Form.Control value={dados.email} readOnly disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="perfil-nivel">
                  <Form.Label>Nível</Form.Label>
                  <Form.Control value={NIVEL_ROTULO[dados.nivel] || dados.nivel} readOnly disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="perfil-celular">
                  <Form.Label>Celular</Form.Label>
                  <Form.Control value={v.celular} onChange={(e) => set('celular', e.target.value)} placeholder="(00) 0 0000-0000" autoComplete="off" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="perfil-telegram">
                  <Form.Label>Telegram</Form.Label>
                  <Form.Control value={v.telegram} onChange={(e) => set('telegram', e.target.value)} autoComplete="off" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="perfil-trello">
                  <Form.Label>E-mail no Trello</Form.Label>
                  <Form.Control type="email" value={v.email_trello} onChange={(e) => set('email_trello', e.target.value)} autoComplete="off" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="perfil-creci">
                  <Form.Label>CRECI</Form.Label>
                  <Form.Control value={v.creci} onChange={(e) => set('creci', e.target.value)} autoComplete="off" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="perfil-estado">
                  <Form.Label>Estado</Form.Label>
                  <Form.Select value={v.estado} onChange={(e) => set('estado', e.target.value)}>
                    <option value="">Selecione...</option>
                    {ESTADOS_BR.map(([sigla, nome]) => (
                      <option key={sigla} value={sigla}>
                        {nome}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>Modelos de mensagem</Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group controlId="perfil-msg-email">
                  <Form.Label>Modelo de mensagem de e-mail</Form.Label>
                  <Form.Control as="textarea" rows={3} value={v.modelo_msg_email} onChange={(e) => set('modelo_msg_email', e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="perfil-msg-whats">
                  <Form.Label>Modelo de mensagem de WhatsApp</Form.Label>
                  <Form.Control as="textarea" rows={3} value={v.modelo_msg_whats} onChange={(e) => set('modelo_msg_whats', e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="perfil-msg-sms">
                  <Form.Label>Modelo de mensagem SMS</Form.Label>
                  <Form.Control as="textarea" rows={3} value={v.modelo_msg_sms} onChange={(e) => set('modelo_msg_sms', e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button type="submit" variant="success" disabled={salvando}>
            {salvando ? <Spinner animation="border" size="sm" /> : 'Salvar'}
          </Button>
          <Button as={Link} to="/trocar-senha" variant="outline-secondary">
            Alterar minha senha
          </Button>
        </div>
      </Form>

      {usuario?.permissoes?.bot_telegram && <div className="mt-3"><CardTelegram /></div>}
      {usuario?.permissoes?.bot_whatsapp && <div className="mt-3"><CardWhatsapp /></div>}
    </>
  );
}
